import moment from 'moment';
import round from 'lodash/round';

function numberClass(number) {
  const rounded = Math.round(number);
  if (rounded > 4) {
    return 'score-color-five';
  }
  if (rounded > 3) {
    return 'score-color-four';
  }
  if (rounded > 2) {
    return 'score-color-three';
  }
  if (rounded > 1) {
    return 'score-color-two';
  }
  return 'score-color-one';
}

/*
 * Formats a given dateTime such that today is a time, yesterday/tomorrow the word, all else just the date.
 */
function formatDateTime(dateTime) {
  const momentDate = moment(dateTime);
  return momentDate.calendar(null, {
    sameDay: 'h:mm a',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: 'M/D/YY',
    sameElse: 'M/D/YY',
  });
}

/**
 * Returns the average score, null if there are no scores at all
 */
function reduceScores(scores) {
  // [points, cards]
  const [points, skills] = Object.values(scores).reduce(
    (accum, current) => [accum[0] + parseFloat(current['skillAverage']), accum[1] + 1],
    [0, 0]
  );
  return skills > 0 ? points / skills : null;
}

function formatScore(score, decimals = 0) {
  const floatScore = parseFloat(score);
  return round(floatScore, decimals);
}

function formatPercentScore(score) {
  const floatScore = parseFloat(score);
  return round(floatScore * 20);
}

function formatDecimal(value, decimals = 2, force = false) {
  if (round(value) !== value || force) {
    return round(value, decimals);
  }
  return round(value);
}

const TIMEFRAME_ENUM = Object.freeze({
  days7: '7 Days',
  weeks2: '2 Weeks',
  weeks3: '3 Weeks',
  weeks4: '4 Weeks',
});

const TIMEFRAME_OPTIONS = Object.entries(TIMEFRAME_ENUM).map(([key, value]) => {
  return {text: value, key: value};
});

function getPeriodDays(value) {
  const periodMap = {};
  periodMap[TIMEFRAME_ENUM.days7] = 7;
  periodMap[TIMEFRAME_ENUM.weeks2] = 14;
  periodMap[TIMEFRAME_ENUM.weeks3] = 21;
  periodMap[TIMEFRAME_ENUM.weeks4] = 28;

  return periodMap[value.key];
}

/**
 * Helper for toggling the sortDirection
 * @param direction
 * @returns {string}
 * @private
 */
function toggleSortDirection(direction) {
  return direction === 'asc' ? 'desc' : 'asc';
}

/**
 * Compares to generic items
 * @param a first item to compare
 * @param b second item to compare
 * @param sortDirection asc / desc
 * @returns {number}
 */
function sortCompare(a, b, sortDirection) {
  // null is always bigger, no matter the sort direction
  // (meaning that they will always be at the bottom of the table)
  if (a == null && b == null) return 0;
  if (a == null && b != null) return 1;
  if (a != null && b == null) return -1;
  return sortDirection === 'desc' ? (a > b ? -1 : 1) : a < b ? -1 : 1;
}

function compareName(a, b, sortDirection) {
  if (a == null && b == null) return 0;
  if (a == null && b != null) return 1;
  if (a != null && b == null) return -1;
  return a.localeCompare(b) * (sortDirection === 'desc' ? -1 : 1);
}

/**
 * used to build the drop down list
 * @param daysBack number of days to subtract from the current date
 * @returns [{key: moment.Moment, text: string}]
 */
function dailyTimeframeOptions(daysBack) {
  return [...Array(daysBack).keys()].map(i => {
    const key = moment().startOf('day').subtract(i, 'day');
    const text = key.calendar(null, {
      sameDay: '[Today]',
      nextDay: '[Tomorrow]',
      nextWeek: 'dddd',
      lastDay: '[Yesterday]',
      lastWeek: '[Last] dddd',
      sameElse: 'DD/MM/YYYY',
    });
    return {key, text};
  });
}

/**
 * first sort by number of occurrences, then alphabetically
 * @param skills
 * @param skillCounts
 * @returns Array
 */
function sortSkills(skills, skillCounts, alphabeticalOnly) {
  return skills.sort((a, b) => {
    if (alphabeticalOnly) {
      return a.localeCompare(b);
    }
    const skillCount = skillCounts[b] - skillCounts[a];
    return skillCount !== 0 ? skillCount : a.localeCompare(b);
  });
}

const TRENDLINE_COLORS = [
  '#2CA8B9',
  '#E44A9C',
  '#FA633C',
  '#7C7C7C',
  '#2979FF',
  '#e96d6d',
  '#60A862',
  '#7951B1',
];

export {
  compareName,
  numberClass,
  formatDateTime,
  reduceScores,
  getPeriodDays,
  formatScore,
  formatPercentScore,
  TIMEFRAME_OPTIONS,
  TIMEFRAME_ENUM,
  toggleSortDirection,
  sortCompare,
  dailyTimeframeOptions,
  sortSkills,
  formatDecimal,
  TRENDLINE_COLORS,
};
